import React, { useState, useEffect, useRef } from 'react'
import generalController from '../../../controllers/generalController'
import { convertPostalToCoordinates } from '../../../utils/googleMapHelper'
import { fetchDealerData, fetchModelData } from '../../../controllers/ActionController'
import TradeInEstimator from './TradeInEstimator'
import { localStorageKeys } from '../../../utils/constants'
import { setItemOnLocalStorage } from '../../../utils/helpers'

interface VehicleData {
  id: number | string
  year: number | string
  model: string
  modelYear?: number
  msrp: number
  title?: string
  description?: string
  visual?: any
  isEV?: boolean
}

interface DealerData {
  dealer_code?: string | number
  dealer?: {
    name?: string
    [key: string]: any
  }
  userPosition?: {
    lat: number
    lng: number
  }
  [key: string]: any
}

interface UserLocation {
  lat: number
  lng: number
}

interface TradeInEstimatorContainerProps {
  formExtraParams?: any
  [key: string]: any
}

declare global {
  interface Window {
    mzFunc?: {
      checkCookie: (key: string) => string | null
    }
  }
}

// HOC for "TradeInEstimator" component
const TradeInEstimatorContainer: React.FC<TradeInEstimatorContainerProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null);
  const [dealerData, setDealerData] = useState<DealerData | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);

  const componentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    handleInitData();
  }, [])

  // Handle initial data with URL params.
  const handleInitData = async () => {
    const params = generalController.getURLSearchParams();

    let dealerCode: string | undefined = params.dealerCode;
    if (typeof window.mzFunc === 'object' && !dealerCode) {
       const cookieValue = window.mzFunc.checkCookie('preferredDealer');
       if (cookieValue) {
         dealerCode = cookieValue;
       }
    }
   

    let initUserPosition: UserLocation | null = null;

    // Vehicle
    if (params.model) {
      const modelParam = decodeURI(params.model).trim();
      const modelParamData = modelParam.split('|');

      if (modelParamData[0] && modelParamData[1]) {
        const initVehicleData = await fetchModelData(modelParamData[0], modelParamData[1]);

        if (initVehicleData) {
          setVehicleData(initVehicleData);
        }
      }
    }

    // Dealer
    if (dealerCode !== undefined && dealerCode !== '') {
      const dealerData = await fetchDealerData(dealerCode);

      if (dealerData && dealerData.dealer) {
        const { userPosition } = dealerData;

        setItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER, dealerData.dealer)
        setDealerData(dealerData);

        // Set dealer geolocation as user location (map center).
        if (userPosition && (userPosition.lat && userPosition.lng)) {
          initUserPosition = userPosition;
        }
      }
    }

    // Location
    if (!initUserPosition) {
      if (params.geolocation) {
        const locationParam = decodeURI(params.geolocation).trim();
        const locationParamDecoded = locationParam.split('|');

        if (locationParamDecoded[0] && locationParamDecoded[1]) {
          initUserPosition = { lat: Number(locationParamDecoded[0]), lng: Number(locationParamDecoded[1]) };
        }
      } else if (params.postalcode) {
        initUserPosition = await convertPostalToCoordinates(params.postalcode);
      }
    }

    if (initUserPosition) {
      setUserLocation(initUserPosition);
    }

    setIsLoading(false);
  }

  return (
    <div className="mz-trade-in-estimator-container  mz-page-top" ref={componentRef}>
      {
        !isLoading && (
          <TradeInEstimator
            formExtraParams={props.formExtraParams || { source: '', language: 'en' }}
            preDefinedVehicleData={vehicleData}
            preDefinedDealerData={dealerData}
            preDefinedUserLocation={userLocation}
            containerRef={componentRef}
          />
        )
      }
    </div>
  )
}

export default TradeInEstimatorContainer
